import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Divider,
  Chip,
  Avatar,
  Stack,
} from '@mui/material';
import { Google, Lock } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { authApi } from '../services/api';

const Profile: React.FC = () => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    // Validate password requirements
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#\-_+=.,;:~`'"<>{}[\]()\\|/])[A-Za-z\d@$!%*?&#\-_+=.,;:~`'"<>{}[\]()\\|/]+$/;
    if (!passwordRegex.test(passwordForm.newPassword)) {
      setError('Password must be at least 8 characters with uppercase, lowercase, number, and at least one special character');
      return;
    }

    setLoading(true);
    try {
      await authApi.changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      
      showNotification('Password changed successfully', 'success');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswordForm(false);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to change password';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordForm({
      ...passwordForm,
      [e.target.name]: e.target.value,
    });
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'error';
      case 'hr': return 'primary';
      case 'manager': return 'secondary';
      case 'viewer': return 'default';
      default: return 'default';
    }
  };

  // Check if user has OAuth
  const hasOAuth = user?.oauth_provider === 'google';
  const hasPassword = user?.has_password || false;

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        My Profile
      </Typography>

      {/* User Information */}
      <Paper elevation={3} sx={{ p: 4, mb: 3 }}>
        <Stack direction="row" spacing={3} alignItems="center">
          <Avatar
            sx={{ width: 80, height: 80, fontSize: '2rem' }}
            src={user?.avatar_url || undefined}
          >
            {user?.first_name?.[0]}{user?.last_name?.[0]}
          </Avatar>
          <Box>
            <Typography variant="h5">
              {user?.first_name} {user?.last_name}
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              {user?.email}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Chip
                label={user?.role?.toUpperCase()}
                color={getRoleColor(user?.role || 'viewer') as any}
                size="small"
              />
              {hasOAuth && (
                <Chip
                  icon={<Google />}
                  label="Google OAuth"
                  variant="outlined"
                  size="small"
                />
              )}
            </Box>
          </Box>
        </Stack>
      </Paper>

      {/* Authentication Methods */}
      <Paper elevation={3} sx={{ p: 4, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Authentication Methods
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Sign-in Methods:
          </Typography>
          {hasOAuth && (
            <Chip
              icon={<Google />}
              label="Google Account Connected"
              color="success"
              sx={{ m: 0.5 }}
            />
          )}
          {hasPassword && (
            <Chip
              icon={<Lock />}
              label="Password Enabled"
              color="info"
              sx={{ m: 0.5 }}
            />
          )}
        </Box>

        {!hasOAuth && (
          <Alert severity="info" sx={{ mb: 2 }}>
            You can link your Google account for easier sign-in. Contact your administrator for assistance.
          </Alert>
        )}
      </Paper>

      {/* Password Change Section */}
      {hasPassword && (
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h6" gutterBottom>
            Password Settings
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {!showPasswordForm ? (
            <Button
              variant="outlined"
              startIcon={<Lock />}
              onClick={() => setShowPasswordForm(true)}
            >
              Change Password
            </Button>
          ) : (
            <Box component="form" onSubmit={handlePasswordChange}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <TextField
                fullWidth
                type="password"
                name="currentPassword"
                label="Current Password"
                value={passwordForm.currentPassword}
                onChange={handleInputChange}
                margin="normal"
                required
                disabled={loading}
              />

              <TextField
                fullWidth
                type="password"
                name="newPassword"
                label="New Password"
                value={passwordForm.newPassword}
                onChange={handleInputChange}
                margin="normal"
                required
                disabled={loading}
                helperText="At least 8 characters with uppercase, lowercase, number, and special character"
              />

              <TextField
                fullWidth
                type="password"
                name="confirmPassword"
                label="Confirm New Password"
                value={passwordForm.confirmPassword}
                onChange={handleInputChange}
                margin="normal"
                required
                disabled={loading}
                error={passwordForm.confirmPassword !== '' && passwordForm.newPassword !== passwordForm.confirmPassword}
                helperText={
                  passwordForm.confirmPassword !== '' && passwordForm.newPassword !== passwordForm.confirmPassword
                    ? 'Passwords do not match'
                    : ''
                }
              />

              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                >
                  Change Password
                </Button>
                <Button
                  variant="text"
                  onClick={() => {
                    setShowPasswordForm(false);
                    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                    setError('');
                  }}
                  disabled={loading}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      )}

      {/* OAuth-only users */}
      {hasOAuth && !hasPassword && (
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h6" gutterBottom>
            Password Settings
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Alert severity="info">
            You signed up with Google OAuth and don't have a password set. 
            You can continue using Google to sign in, or contact your administrator if you need password access.
          </Alert>
        </Paper>
      )}
    </Container>
  );
};

export default Profile;